import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';

const MESSAGES = [
  'Loading your workspace…',
  'Almost ready…',
];

/**
 * Clean, minimal branded loading screen.
 * Pass `message` for a fixed label, otherwise it cycles through MESSAGES.
 */
export default function LoadingScreen({ message }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (message) return;
    const t = setInterval(() => setIdx(i => (i + 1) % MESSAGES.length), 1500);
    return () => clearInterval(t);
  }, [message]);

  const bg = isDark
    ? 'radial-gradient(120% 120% at 50% 35%, #0b1f3a 0%, #07132a 60%, #03070f 100%)'
    : 'radial-gradient(120% 120% at 50% 35%, #0c4f86 0%, #073a63 60%, #052742 100%)';

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: bg, fontFamily: "'DM Sans', sans-serif",
    }}>
      {/* Logo with a single spinner ring */}
      <div style={{
        position: 'relative', width: 96, height: 96,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 24,
      }}>
        <div className="ls-ring" />
        <div style={{
          width: 64, height: 64, borderRadius: 18,
          background: 'linear-gradient(135deg,#38bdf8,#0284c7 55%,#075985)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 8px 28px rgba(2,132,199,0.45)',
          animation: 'ls-pulse 1.4s ease-in-out infinite',
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
            <path d="M13 5v2" /><path d="M13 11v2" /><path d="M13 17v2" />
          </svg>
        </div>
      </div>

      {/* Wordmark */}
      <div style={{ fontSize: 24, fontWeight: 800, color: '#fff', letterSpacing: '-0.5px' }}>ITSM Tool</div>
      <div style={{ fontSize: 11.5, fontWeight: 500, color: 'rgba(255,255,255,0.6)', letterSpacing: '1.5px', marginTop: 5 }}>
        SERVICE MANAGEMENT PLATFORM
      </div>

      {/* Thin progress bar */}
      <div style={{ marginTop: 26, width: 200, height: 4, borderRadius: 4, background: 'rgba(255,255,255,0.14)', overflow: 'hidden' }}>
        <div className="ls-bar" />
      </div>

      {/* Status message */}
      <div key={message || idx} style={{ marginTop: 14, fontSize: 12.5, fontWeight: 500, color: 'rgba(255,255,255,0.55)' }}>
        {message || MESSAGES[idx]}
      </div>

      <style>{`
        .ls-ring {
          position: absolute; inset: 0; border-radius: 50%;
          border: 3px solid rgba(255,255,255,0.10);
          border-top-color: #38bdf8;
          animation: ls-spin 0.6s linear infinite;
          box-shadow: 0 0 18px rgba(56,189,248,0.5);
        }
        .ls-ring::before {
          content: ''; position: absolute; inset: -12px; border-radius: 50%;
          box-shadow: 0 0 32px 6px rgba(56,189,248,0.4);
          animation: ls-halo 1.8s ease-in-out infinite;
        }
        .ls-bar {
          width: 40%; height: 100%; border-radius: 4px;
          background: linear-gradient(90deg, transparent, #38bdf8, #7dd3fc, transparent);
          animation: ls-slide 0.8s ease-in-out infinite;
        }
        @keyframes ls-spin  { to { transform: rotate(360deg); } }
        @keyframes ls-slide { 0% { transform: translateX(-120%); } 100% { transform: translateX(320%); } }
        @keyframes ls-pulse { 0%,100% { transform: scale(1) rotate(0deg); } 50% { transform: scale(1.12) rotate(4deg); } }
        @keyframes ls-halo  { 0%,100% { opacity: .35; } 50% { opacity: 1; } }
      `}</style>
    </div>
  );
}
